import { useCallback, useEffect, useState } from "react";
import api from "../api";
import { useAuth } from "../AuthContext";
import CaptureForm from "../components/marketing/CaptureForm";
import ContentBuilder from "../components/marketing/ContentBuilder";
import InfoPage from "../components/marketing/InfoPage";
import MarketingTasks from "../components/marketing/MarketingTasks";
import Outreach from "../components/marketing/Outreach";
import Playbook from "../components/marketing/Playbook";
import Queue from "../components/marketing/Queue";

// Tasks first, and the default: "what needs me?" is the question Ward opens
// this section with, and the Playbook cannot answer it — the cards explain how
// the work is done, not what is outstanding.
const TABS = [
  { key: "tasks", label: "Tasks" },
  { key: "playbook", label: "Playbook" },
  { key: "queue", label: "Queue" },
  { key: "capture", label: "Capture" },
  { key: "outreach", label: "Outreach" },
  { key: "builder", label: "Content builder" },
  // Reference pages: read-only, nothing to act on.
  { key: "who-buys", label: "Who buys DS", info: true },
  { key: "brand-bible", label: "Brand bible", info: true },
  { key: "ward-deck", label: "Findings deck", info: true },
];

export default function MarketingView({ goHome }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState("tasks");
  const [counts, setCounts] = useState(null);
  // Set by the drawer's approve checklist: which Playbook card, and which of
  // the checks inside it, to open and scroll to. Cleared by the
  // Playbook once it has acted on it, so the same check can be opened twice.
  const [playbookFocus, setPlaybookFocus] = useState(null);

  const openCheck = useCallback((slug, checkId) => {
    setPlaybookFocus({ slug, checkId });
    setTab("playbook");
  }, []);

  const clearPlaybookFocus = useCallback(() => setPlaybookFocus(null), []);

  const loadSummary = useCallback(() => {
    return api.get("/marketing/summary")
      .then(({ data }) => setCounts(data.counts))
      .catch(() => setCounts(null));
  }, []);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  // Everything except published and killed is work in hand.
  const working = counts
    ? counts.idea + counts.drafting + counts.review + counts.queued
    : null;

  return (
    <div className="view" data-testid="marketing-view">
      <div className="crumb">
        <a onClick={goHome} data-testid="marketing-back-home">← Home</a>
        {" / "}
        Marketing
      </div>

      <div className="sec-head">Marketing</div>
      <div className="sec-desc">
        What is outstanding, how the job is done, and everything moving from
        idea to published.
      </div>

      <div className="mk-tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            className={`mk-tab ${tab === t.key ? "on" : ""} ${t.info ? "info" : ""}`}
            onClick={() => setTab(t.key)}
            data-testid={`mk-tab-${t.key}`}
          >
            {t.label}
            {t.key === "queue" && working !== null && (
              <span className="mk-count">{working}</span>
            )}
          </button>
        ))}
      </div>

      {tab === "tasks" && <MarketingTasks isAdmin={isAdmin} />}
      {tab === "playbook" && (
        <Playbook isAdmin={isAdmin} onOpenCapture={() => setTab("capture")}
          focus={playbookFocus} onFocusHandled={clearPlaybookFocus} />
      )}
      {tab === "queue" && (
        <Queue isAdmin={isAdmin} onChanged={loadSummary} onOpenCheck={openCheck} />
      )}
      {/* Mounted like Capture (hidden, not unmounted) so a pasted draft and its
          results survive a trip to the Queue to look at what was just added. */}
      <div hidden={tab !== "builder"}><ContentBuilder onQueued={loadSummary} /></div>
      {tab === "outreach" && <Outreach />}
      {tab === "who-buys" && <InfoPage slug="who-buys" title="Who buys DS" />}
      {tab === "brand-bible" && <InfoPage slug="brand-bible" title="Brand bible" />}
      {tab === "ward-deck" && <InfoPage slug="ward-deck" title="Findings deck" />}
      {/*
        Capture stays mounted even on other tabs (hidden via the `hidden`
        attribute, not unmounted) so a half-typed draft survives Ward
        tapping over to the Queue to check something and coming back.
        CaptureForm does no work on mount — only inside its own submit
        handler — so keeping it mounted while hidden is inert.
      */}
      <CaptureForm onDone={loadSummary} hidden={tab !== "capture"} />
    </div>
  );
}
